import { useState, type FormEvent } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api } from '@/api/client';
import './Login.css'; // re-uses Login card styling

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);

    if (!token) {
      setError('Reset token is missing from the URL.');
      return;
    }
    if (!password) {
      setError('Password is required.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/api/auth/reset-password', {
        newPassword: password,
        token: token,
      });
      setSuccess(true);
    } catch (err) {
      const e = err as { message?: string };
      setError(e.message || 'Failed to reset password. The link may be expired or already used.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="login">
      <div className="login__card">
        <header className="login__header">
          <img className="login__logo" src="/brew-logo.png" alt="Breww.ai" width={108} height={108} />
          <h1>Reset Password</h1>
          <p>Please enter your new password below.</p>
        </header>

        {!token ? (
          <div className="login__form">
            <div className="login__error">
              Invalid or missing password reset token. Please request a new reset link.
            </div>
            <Link to="/forgot-password" className="login__submit" style={{ textDecoration: 'none', textAlign: 'center', display: 'block' }}>
              Request new link
            </Link>
          </div>
        ) : success ? (
          <div className="login__form">
            <div style={{
              background: 'color-mix(in srgb, var(--color-accent) 10%, transparent)',
              border: '1px solid color-mix(in srgb, var(--color-accent) 30%, transparent)',
              color: 'var(--text)',
              padding: '1rem',
              borderRadius: 'var(--radius-sm, 8px)',
              fontSize: '0.9rem',
              lineHeight: '1.4',
              textAlign: 'center'
            }}>
              Your password has been reset successfully. You can now log in with your new password.
            </div>
            <Link to="/login" className="login__submit" style={{ textDecoration: 'none', textAlign: 'center', display: 'block' }}>
              Sign In
            </Link>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="login__form" noValidate>
            <label className="login__field">
              <span>New Password</span>
              <div className="login__input-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoFocus
                />
                <button
                  type="button"
                  className="login__eye"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                  tabIndex={-1}
                >
                  {showPassword ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c6.5 0 10 7 10 7a13.2 13.2 0 0 1-1.67 2.68"/><path d="M6.06 6.06C3.4 7.7 2 12 2 12s3.5 7 10 7a9.7 9.7 0 0 0 3.5-.64"/><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/><path d="M2 2l20 20"/></svg>
                  )}
                </button>
              </div>
            </label>
            <label className="login__field">
              <span>Confirm Password</span>
              <div className="login__input-wrap">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                />
                <button
                  type="button"
                  className="login__eye"
                  onClick={() => setShowConfirm((v) => !v)}
                  aria-label={showConfirm ? 'Hide password' : 'Show password'}
                  aria-pressed={showConfirm}
                  tabIndex={-1}
                >
                  {showConfirm ? (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7-10-7-10-7z"/><circle cx="12" cy="12" r="3"/></svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c6.5 0 10 7 10 7a13.2 13.2 0 0 1-1.67 2.68"/><path d="M6.06 6.06C3.4 7.7 2 12 2 12s3.5 7 10 7a9.7 9.7 0 0 0 3.5-.64"/><path d="M9.9 9.9a3 3 0 0 0 4.2 4.2"/><path d="M2 2l20 20"/></svg>
                  )}
                </button>
              </div>
            </label>
            {error && <div className="login__error">{error}</div>}
            <button type="submit" className="login__submit" disabled={submitting}>
              {submitting ? 'Resetting password…' : 'Reset password'}
            </button>
          </form>
        )}

        {!success && (
          <footer className="login__footer">
            <Link className="login__link login__link--accent" to="/login">Back to Sign In</Link>
          </footer>
        )}
      </div>
    </div>
  );
}
